import React, { useState } from "react";
import axios from "axios";
import { MessageCircle, Loader2, CheckCircle2, Sparkles } from "lucide-react";
import { buildWaLink } from "@/lib/whatsapp";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Generic form with WhatsApp alternative.
 *
 * Props:
 *   endpoint: backend route under /api (e.g. "/forms/technical-support")
 *   fields:   array of { name, label, type, required, options, placeholder, textarea, rows, helper, full }
 *   submitLabel: string
 *   waMessageBuilder: (values) => string  // builds WhatsApp pre-filled message
 *   testidPrefix: string (e.g. "tech-support")
 *   aiDraft:  optional { topic, targetField?, roleField? } — enables the AI drafter
 */
export default function FormWithWhatsApp({
  endpoint,
  fields,
  submitLabel = "Submit",
  waMessageBuilder,
  testidPrefix = "form",
  successText = "Thanks — the Biodeal Animal Health team will be in touch shortly.",
  aiDraft = null,
}) {
  const [values, setValues] = useState({});
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const [error, setError] = useState(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiBrief, setAiBrief] = useState("");
  const [aiLoading, setAiLoading] = useState(false);

  const update = (name, value) => {
    setValues((prev) => ({ ...prev, [name]: value }));
  };

  const draftWithAi = async () => {
    if (!aiDraft || !aiBrief.trim() || aiLoading) return;
    setAiLoading(true);
    try {
      const roleHint = aiDraft.roleField
        ? values[aiDraft.roleField] || undefined
        : undefined;
      const { data } = await axios.post(`${API}/ai/draft-message`, {
        topic: aiDraft.topic,
        role_hint: roleHint,
        brief: aiBrief.trim(),
      });
      update(aiDraft.targetField || "message", data.draft);
      setAiOpen(false);
      setAiBrief("");
    } catch (err) {
      // Silent fail — user can still write manually.
      setAiOpen(false);
    } finally {
      setAiLoading(false);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus("submitting");
    setError(null);
    try {
      // Strip empty optional fields
      const payload = Object.fromEntries(
        Object.entries(values).filter(([_, v]) => v !== "" && v !== undefined && v !== null)
      );
      await axios.post(`${API}${endpoint}`, payload);
      setStatus("success");
    } catch (err) {
      console.error(err);
      const detail =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong — please WhatsApp us as an alternative.";
      setError(typeof detail === "string" ? detail : "Submission failed.");
      setStatus("error");
    }
  };

  if (status === "success") {
    return (
      <div
        className="bg-white border border-bah-green/30 rounded-2xl p-8 text-center"
        data-testid={`${testidPrefix}-success`}
      >
        <CheckCircle2 className="h-10 w-10 text-bah-green mx-auto" />
        <h3 className="mt-4 font-display text-2xl text-bah-ink">Submission received.</h3>
        <p className="mt-2 text-bah-mute text-sm max-w-md mx-auto">{successText}</p>
        <button
          type="button"
          onClick={() => {
            setValues({});
            setStatus("idle");
          }}
          className="mt-6 btn-ghost-dark"
          data-testid={`${testidPrefix}-reset`}
        >
          Submit another
        </button>
      </div>
    );
  }

  const waLink = waMessageBuilder ? buildWaLink(waMessageBuilder(values)) : null;

  return (
    <form
      onSubmit={onSubmit}
      className="bg-white border border-bah-line rounded-2xl p-6 md:p-8 shadow-sm"
      data-testid={`${testidPrefix}-form`}
    >
      {aiDraft && (
        <div
          className="mb-5 rounded-xl bg-bah-paper border border-bah-line p-4"
          data-testid={`${testidPrefix}-ai-drafter`}
        >
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] font-bold text-bah-ink/80">
              <Sparkles className="h-3.5 w-3.5 text-emerald-600" />
              AI helper
            </div>
            <button
              type="button"
              onClick={() => setAiOpen((o) => !o)}
              data-testid={`${testidPrefix}-ai-toggle`}
              className="text-xs font-semibold text-bah-green hover:text-bah-orange"
            >
              {aiOpen ? "Close AI helper" : "✨ Draft my message with AI"}
            </button>
          </div>
          {aiOpen && (
            <div className="mt-3 space-y-2">
              <textarea
                rows={2}
                placeholder="A quick 1-line brief — e.g. Need BIOTRIM VET PLUS advice for a broiler flock in Kiambu"
                value={aiBrief}
                onChange={(e) => setAiBrief(e.target.value)}
                data-testid={`${testidPrefix}-ai-brief`}
                className="w-full px-3 py-2 rounded-lg border border-bah-line bg-white text-sm focus:outline-none focus:border-bah-green focus:ring-2 focus:ring-bah-green/30"
              />
              <button
                type="button"
                onClick={draftWithAi}
                disabled={!aiBrief.trim() || aiLoading}
                data-testid={`${testidPrefix}-ai-generate`}
                className="inline-flex items-center gap-2 rounded-lg bg-bah-ink text-white text-xs font-bold px-3 py-2 disabled:opacity-50 hover:bg-bah-ink/90"
              >
                {aiLoading ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 animate-spin" /> Drafting…
                  </>
                ) : (
                  <>
                    <Sparkles className="h-3.5 w-3.5" /> Generate draft
                  </>
                )}
              </button>
              <p className="text-[11px] text-bah-mute">
                The AI will fill the message field. You can edit it before
                submitting. Grounded in our catalogue.
              </p>
            </div>
          )}
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {fields.map((f) => (
          <div
            key={f.name}
            className={f.full || f.textarea ? "md:col-span-2" : ""}
          >
            <label
              htmlFor={`${testidPrefix}-${f.name}`}
              className="block text-[11px] font-bold uppercase tracking-[0.18em] text-bah-ink mb-1.5"
            >
              {f.label} {f.required && <span className="text-bah-orange">*</span>}
            </label>
            {f.textarea ? (
              <textarea
                id={`${testidPrefix}-${f.name}`}
                name={f.name}
                rows={f.rows || 4}
                required={f.required}
                placeholder={f.placeholder}
                value={values[f.name] || ""}
                onChange={(e) => update(f.name, e.target.value)}
                data-testid={`${testidPrefix}-input-${f.name}`}
                className="w-full px-4 py-3 rounded-xl border border-bah-line bg-white focus:outline-none focus:border-bah-green focus:ring-2 focus:ring-bah-green/30 text-sm text-bah-ink"
              />
            ) : f.options ? (
              <select
                id={`${testidPrefix}-${f.name}`}
                name={f.name}
                required={f.required}
                value={values[f.name] || ""}
                onChange={(e) => update(f.name, e.target.value)}
                data-testid={`${testidPrefix}-input-${f.name}`}
                className="w-full px-4 py-3 rounded-xl border border-bah-line bg-white focus:outline-none focus:border-bah-green focus:ring-2 focus:ring-bah-green/30 text-sm text-bah-ink"
              >
                <option value="">Select…</option>
                {f.options.map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </select>
            ) : (
              <input
                id={`${testidPrefix}-${f.name}`}
                name={f.name}
                type={f.type || "text"}
                required={f.required}
                placeholder={f.placeholder}
                value={values[f.name] || ""}
                onChange={(e) => update(f.name, e.target.value)}
                data-testid={`${testidPrefix}-input-${f.name}`}
                className="w-full px-4 py-3 rounded-xl border border-bah-line bg-white focus:outline-none focus:border-bah-green focus:ring-2 focus:ring-bah-green/30 text-sm text-bah-ink"
              />
            )}
            {f.helper && (
              <div className="mt-1 text-xs text-bah-mute">{f.helper}</div>
            )}
          </div>
        ))}
      </div>

      {error && (
        <div
          className="mt-4 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700"
          data-testid={`${testidPrefix}-error`}
        >
          {error}
        </div>
      )}

      <div className="mt-6 flex flex-col sm:flex-row gap-3">
        <button
          type="submit"
          disabled={status === "submitting"}
          data-testid={`${testidPrefix}-submit`}
          className="btn-primary disabled:opacity-60"
        >
          {status === "submitting" ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" /> Sending…
            </>
          ) : (
            submitLabel
          )}
        </button>
        {waLink && (
          <a
            href={waLink}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-whatsapp"
            data-testid={`${testidPrefix}-whatsapp`}
          >
            <MessageCircle className="h-4 w-4" /> Prefer WhatsApp? Send now
          </a>
        )}
      </div>
    </form>
  );
}
